import { type CSSProperties } from 'preact'
import type { Dispatch, StateUpdater } from 'preact/hooks'

import { getCookieDomainFromWebsiteUrl } from '#src/assets/core/consentConfig.ts'
import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import { CookieCategoryRow } from '#src/assets/ui/preferences/components/CookieCategoryRow.tsx'
import { Heading } from '#src/assets/ui/shared/components/Heading.tsx'
import { Paragraph } from '#src/assets/ui/shared/components/Paragraph.tsx'
import { scrollableBodyStyle } from '#src/assets/ui/shared/styles.ts'
import type { SiteConfig } from '~/src/models/site-configs/siteConfigs.ts'
import type {
  CookieCategory,
  CookieRecord,
} from '~/src/services/cookieTranslationService/types.ts'

export function PreferencesModalBody({
  t,
  siteConfig,
  cookiesByCategory,
  configuredCategories,
  categoryState,
  setCategoryState,
}: {
  t: CookieConsentTranslator
  siteConfig: SiteConfig
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>
  configuredCategories: CookieCategory[]
  categoryState: Record<string, boolean>
  setCategoryState: Dispatch<StateUpdater<Record<string, boolean>>>
}) {
  const cookieDomain = getCookieDomainFromWebsiteUrl(siteConfig.domain)

  return (
    <div style={bodyStyle}>
      <Heading level={3} variant="subtitle">
        {t('preferencesIntroTitle')}
      </Heading>
      <Paragraph variant="body">
        {t('preferencesIntroDescription', {
          coachName: siteConfig.coachName,
          policyUrl: siteConfig.cookiePolicyUrl,
        })}
      </Paragraph>
      <div style={categoryListStyle}>
        {configuredCategories.map(category => (
          <CookieCategoryRow
            key={category}
            category={category}
            cookies={cookiesByCategory[category]}
            fallbackDomain={cookieDomain}
            t={t}
            checked={categoryState[category] ?? false}
            readOnly={category === 'necessary'}
            onToggle={enabled => {
              if (category === 'necessary') {
                return
              }

              setCategoryState(prev => ({
                ...prev,
                [category]: enabled,
              }))
            }}
          />
        ))}
      </div>
    </div>
  )
}

const bodyStyle: CSSProperties = {
  ...scrollableBodyStyle,
  padding: '8px 24px 20px',
}

const categoryListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}
